import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, Users, Clock, Image, AlertCircle, Info, Map, Upload, Navigation } from 'lucide-react';

import LocationPicker from './LocationPicker';
import { useAuth } from '../contexts/AuthContext';
import { getGoogleMapsApiKey } from '../config/api';

interface Event {
  EVENT_ID?: number;
  id?: number;
  TITLE?: string;
  title?: string;
  DESCRIPTION?: string;
  description?: string;
  LOCATION?: string;
  location?: string;
  ADDRESS?: string;
  address?: string;
  LATITUDE?: number;
  latitude?: number;
  LONGITUDE?: number;
  longitude?: number;
  START_DATE?: string;
  startDate?: string;
  END_DATE?: string;
  endDate?: string;
  EXPECTED_ATTENDANCE?: number;
  expectedAttendance?: number;
  IS_VERIFIED?: boolean;
  isVerified?: boolean;
  STATUS?: string;
  status?: string;
  IS_FEATURED?: boolean;
  isFeatured?: boolean;
  IS_POPULAR?: boolean;
  isPopular?: boolean;
  CREATED_BY?: number;
  createdBy?: number;
  IMAGES?: string[]; // Array of image URLs
  images?: string[]; // Array of image URLs
  IMAGE_URL?: string; // Single image URL for thumbnail
  imageUrl?: string; // Single image URL for thumbnail
  originalEvent?: any; // Original event data from API
}

interface EventFormProps {
  event?: Event | null;
  onClose: () => void;
  onSubmit: (eventData: any) => Promise<void>;
  loading: boolean;
  title: string;
}

interface LocationData {
  latitude: number;
  longitude: number;
  address: string;
  location: string;
}

const EventFormWithMap: React.FC<EventFormProps> = ({
  event,
  onClose,
  onSubmit,
  loading,
  title
}) => {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'ADMIN';
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    address: '',
    startDate: '',
    endDate: '',
    expectedAttendance: 0,
    latitude: 0,
    longitude: 0,
  });

  // Initialize form data immediately when event prop changes
  React.useEffect(() => {
    if (event) {
      // Check if we have originalEvent with full data
      const sourceEvent = event.originalEvent || event;
      
      // Direct assignment approach - check both originalEvent and event
      const newFormData = {
        title: sourceEvent.TITLE || sourceEvent.title || event.TITLE || event.title || '',
        description: sourceEvent.DESCRIPTION || sourceEvent.description || event.DESCRIPTION || event.description || '',
        location: sourceEvent.LOCATION || sourceEvent.location || event.LOCATION || event.location || '',
        address: sourceEvent.ADDRESS || sourceEvent.address || event.ADDRESS || event.address || '',
        startDate: sourceEvent.START_DATE || sourceEvent.startDate ? new Date(sourceEvent.START_DATE || sourceEvent.startDate || '').toISOString().slice(0, 16) : '',
        endDate: sourceEvent.END_DATE || sourceEvent.endDate ? new Date(sourceEvent.END_DATE || sourceEvent.endDate || '').toISOString().slice(0, 16) : '',
        expectedAttendance: sourceEvent.EXPECTED_ATTENDANCE || sourceEvent.expectedAttendance || event.EXPECTED_ATTENDANCE || event.expectedAttendance || 0,
        latitude: Number(sourceEvent.LATITUDE || sourceEvent.latitude || event.LATITUDE || event.latitude || 0),
        longitude: Number(sourceEvent.LONGITUDE || sourceEvent.longitude || event.LONGITUDE || event.longitude || 0),
      };
      
      setFormData(newFormData);
    }
  }, [event]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [existingThumbnail, setExistingThumbnail] = useState<string | null>(null);

  // Admin-only: User info to create user with event
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    role: 'COMMITTEE'
  });

  // Set existing thumbnail if available
  useEffect(() => {
    if (event && (event.IMAGE_URL || event.imageUrl)) {
      setExistingThumbnail(event.IMAGE_URL || event.imageUrl || '');
    }
  }, [event]);


  // Cleanup thumbnail preview on unmount
  useEffect(() => {
    return () => {
      if (thumbnailPreview) {
        URL.revokeObjectURL(thumbnailPreview);
      }
    };
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Event title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Event description is required';
    }

    // Location and address are automatically populated from map selection, so we check coordinates
    if (formData.latitude === 0 || formData.longitude === 0) {
      newErrors.location = 'Please select a location from the map';
      newErrors.address = 'Please select a location from the map to automatically fill the address';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) >= new Date(formData.endDate)) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    if (formData.expectedAttendance < 1) {
      newErrors.expectedAttendance = 'Expected attendance must be at least 1';
    }

    // Validate coordinates if provided
    if (formData.latitude !== 0 || formData.longitude !== 0) {
      if (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90) {
        newErrors.latitude = 'Latitude must be between -90 and 90';
      }
      if (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180) {
        newErrors.longitude = 'Longitude must be between -180 and 180';
      }
    }

    // Validate thumbnail if provided
    if (thumbnail) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (thumbnail.size > maxSize) {
        newErrors.thumbnail = 'Thumbnail must be less than 2MB';
      }
      
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(thumbnail.type)) {
        newErrors.thumbnail = 'Thumbnail must be JPEG, PNG, or WebP format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Create FormData object for the API
      const submitData = new FormData();
      
      // Add text fields
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('location', formData.location);
      submitData.append('address', formData.address);
      submitData.append('startDate', new Date(formData.startDate).toISOString());
      submitData.append('endDate', new Date(formData.endDate).toISOString());
      submitData.append('expectedAttendance', formData.expectedAttendance.toString());
      
      // Only add coordinates if they are valid
      if (formData.latitude !== 0 && formData.longitude !== 0) {
        submitData.append('latitude', formData.latitude.toString());
        submitData.append('longitude', formData.longitude.toString());
      }

      // Add thumbnail file if provided
      if (thumbnail) {
        submitData.append('thumbnail', thumbnail);
      }

      // If admin and user tab has values, attach user info to drive create-with-user API
      if (isAdmin && (userInfo.firstName || userInfo.lastName || userInfo.phone || userInfo.email)) {
        if (userInfo.firstName) submitData.append('userFirstName', userInfo.firstName);
        if (userInfo.lastName) submitData.append('userLastName', userInfo.lastName);
        if (userInfo.phone) submitData.append('userPhone', userInfo.phone);
        if (userInfo.email) submitData.append('userEmail', userInfo.email);
        if (userInfo.role) submitData.append('userRole', userInfo.role);
      }

      await onSubmit(submitData);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Handle coordinate fields specially
    if (name === 'latitude' || name === 'longitude') {
      const numValue = value === '' ? 0 : parseFloat(value);
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLocationSelect = (locationData: LocationData) => {
    setFormData(prev => ({
      ...prev,
      location: locationData.location,
      address: locationData.address,
      latitude: locationData.latitude,
      longitude: locationData.longitude
    }));
    
    // Clear location-related errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.location;
      delete newErrors.address;
      return newErrors;
    });
    
    setShowLocationPicker(false);
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnail(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setThumbnailPreview(previewUrl);
      
      // Clear thumbnail error if exists
      if (errors.thumbnail) {
        setErrors(prev => ({ ...prev, thumbnail: '' }));
      }
    }
  };

  const handleThumbnailRemove = () => {
    if (thumbnailPreview) {
      URL.revokeObjectURL(thumbnailPreview);
    }
    setThumbnail(null);
    setThumbnailPreview(null);
    setExistingThumbnail(null);
    
    // Clear thumbnail error if exists
    if (errors.thumbnail) {
      setErrors(prev => ({ ...prev, thumbnail: '' }));
    }
  };

  // Function to reverse geocode coordinates and populate location/address
  const handleCoordinateChange = async (lat: number, lng: number) => {
    if (lat !== 0 && lng !== 0) {
      try {
        // Use Google Geocoding API for reverse geocoding
        const apiKey = getGoogleMapsApiKey();
        console.log('EventFormWithMap API Key:', apiKey ? 'Present' : 'Missing', 'Value:', apiKey);
        
        if (!apiKey) {
          console.error('Google Maps API key not found in EventFormWithMap');
          return;
        }
        
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
        );
        const data = await response.json();
        
        if (data.status === 'OK' && data.results.length > 0) {
          const result = data.results[0];
          const address = result.formatted_address;
          const addressComponents = result.address_components;
          
          let city = '';
          let state = '';
          
          addressComponents.forEach((component: any) => {
            if (component.types.includes('locality')) {
              city = component.long_name;
            } else if (component.types.includes('administrative_area_level_1')) {
              state = component.long_name;
            }
          });
          
          const location = city && state ? `${city}, ${state}` : address.split(',')[0] || 'Unknown location';
          
          setFormData(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            location: location,
            address: address
          }));
          
          // Clear location-related errors
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.location;
            delete newErrors.address;
            return newErrors;
          });
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
        // If reverse geocoding fails, just update coordinates
        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng
        }));
      }
    } else {
      // Clear coordinates if invalid
      setFormData(prev => ({
        ...prev,
        latitude: 0,
        longitude: 0
      }));
    }
  };

  // Auto-populate location and address on component mount if no coordinates exist (only for new events)
  useEffect(() => {
    // Only set default coordinates for new events (when event is null/undefined)
    // and only if we don't have any coordinates set
    if (!event && formData.latitude === 0 && formData.longitude === 0) {
      // Set default coordinates for Durga Puja events (Kolkata, West Bengal)
      const defaultLat = 22.5726; // Kolkata, West Bengal - major Durga Puja center
      const defaultLng = 88.3639; // Kolkata, West Bengal
      
      // Auto-populate location and address using default coordinates
      // This ensures the form is never in an invalid state on initialization
      handleCoordinateChange(defaultLat, defaultLng);
    }
  }, [event]); // Run when event changes

  const hasLocationCoordinates = 
    typeof formData.latitude === 'number' && formData.latitude !== 0 &&
    typeof formData.longitude === 'number' && formData.longitude !== 0;

  // Helper function to get event status
  const getEventStatus = (event: Event) => {
    // Only show status for approved/verified events
    if (event.status === 'approved') {
      return {
        isApproved: true,
        displayText: 'Event Approved',
        description: 'This event has been approved and is visible to all users.'
      };
    }
    
    // Fallback to IS_VERIFIED boolean
    if (event.IS_VERIFIED === true) {
      return {
        isApproved: true,
        displayText: 'Event Verified',
        description: 'This event has been verified and is visible to all users.'
      };
    }
    
    // Return null for non-approved/non-verified events (no status display)
    return null;
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-dpn-pink bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-dpn-pink" />
            </div>
            <h2 className="font-runtime text-3xl font-bold text-black mb-2">
              {title}
            </h2>
            <p className="font-lucy-rose text-gray-600">
              {event ? 'Update event details' : 'Add a new event to the Durga Puja Network'}
            </p>
            
            {/* Approval Process Info for Committee Users */}
            {!isAdmin && !event && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 font-lucy-rose mb-1">
                      Event Approval Process
                    </h4>
                    <p className="text-sm text-blue-700 font-lucy-rose">
                      Your event will be submitted for admin approval. Once approved by our team, 
                      it will be visible to all users in the Durga Puja Network.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Status Info for Existing Events - Only show for approved/verified events */}
            {event && (() => {
              const statusInfo = getEventStatus(event);
              // Only display status if statusInfo exists (approved/verified events)
              if (!statusInfo) {
                return null;
              }
              
              return (
                <div className="mt-4 border rounded-lg p-4 bg-green-50 border-green-200">
                  <div className="flex items-start space-x-3">
                    <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium font-lucy-rose mb-1 text-green-800">
                        {statusInfo.displayText}
                      </h4>
                      <p className="text-sm font-lucy-rose text-green-700">
                        {statusInfo.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Admin: show User section inline below the Event section (no tabs) */}

          <form key={event?.id || event?.EVENT_ID || 'new'} onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  Event Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  maxLength={100}
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose ${
                    errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Event title"
                />
                <div className="mt-1 text-xs text-gray-500 text-right font-lucy-rose">{formData.title.length}/100</div>
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600 font-lucy-rose">{errors.title}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  Event Location (Pre-filled from map) {hasLocationCoordinates && <span className="text-green-600">✓</span>}
                </label>
                <div className="space-y-3">
                  {/* Location Display */}
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      value={formData.location}
                      readOnly
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg bg-gray-50 font-lucy-rose ${
                        errors.location ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Location is automatically populated from map coordinates"
                    />
                  </div>
                  
                  {/* Manual Coordinate Input */}
                  <div className="space-y-3">
                    {/* Coordinate Information */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <MapPin className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-800 font-lucy-rose mb-1">
                            Location Coordinates
                          </h4>
                          <p className="text-xs text-blue-700 font-lucy-rose">
                            Enter coordinates manually or use the map below. Coordinates will automatically populate location and address fields.
                          </p>
                          {(formData.latitude !== 0 || formData.longitude !== 0) && (
                            <p className="text-xs text-blue-600 mt-2 font-medium">
                              Current: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Get Current Location Button */}
                    <button
                      type="button"
                      onClick={() => {
                        if (navigator.geolocation) {
                          navigator.geolocation.getCurrentPosition(
                            (position) => {
                              const lat = position.coords.latitude;
                              const lng = position.coords.longitude;
                              handleCoordinateChange(lat, lng);
                            },
                            (error) => {
                              console.warn('Geolocation error:', error);
                              alert('Unable to get current location. Please enter coordinates manually or use the map.');
                            }
                          );
                        } else {
                          alert('Geolocation is not supported by this browser. Please enter coordinates manually or use the map.');
                        }
                      }}
                      className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-lucy-rose text-sm"
                    >
                      <Navigation className="h-4 w-4" />
                      <span>Get Current Location</span>
                    </button>
                    
                    <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1 font-lucy-rose">
                              Latitude
                              <span className="text-xs text-gray-500 ml-1">-90 to 90</span>
                            </label>
                            <input
                              type="number"
                              step="any"
                              min="-90"
                              max="90"
                              placeholder="e.g., 22.5726"
                              value={formData.latitude !== 0 ? formData.latitude : ''}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                if (value !== 0 && formData.longitude !== 0) {
                                  handleCoordinateChange(value, formData.longitude);
                                } else {
                                  setFormData(prev => ({ ...prev, latitude: value }));
                                }
                              }}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose ${
                                errors.latitude ? 'border-red-300 bg-red-50' : 'border-gray-300'
                              }`}
                            />
                            {errors.latitude && (
                              <p className="mt-1 text-xs text-red-600 font-lucy-rose">{errors.latitude}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1 font-lucy-rose">
                              Longitude
                              <span className="text-xs text-gray-500 ml-1">-180 to 180</span>
                            </label>
                            <input
                              type="number"
                              step="any"
                              min="-180"
                              max="180"
                              placeholder="e.g., 88.3639"
                              value={formData.longitude !== 0 ? formData.longitude : ''}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                if (value !== 0 && formData.latitude !== 0) {
                                  handleCoordinateChange(formData.latitude, value);
                                } else {
                                  setFormData(prev => ({ ...prev, longitude: value }));
                                }
                              }}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose ${
                                errors.longitude ? 'border-red-300 bg-red-50' : 'border-gray-300'
                              }`}
                            />
                            {errors.longitude && (
                              <p className="mt-1 text-xs text-red-600 font-lucy-rose">{errors.longitude}</p>
                            )}
                          </div>
                        </div>
                  </div>
                  
                  {/* Clear Coordinates Button */}
                  {(formData.latitude !== 0 || formData.longitude !== 0) && (
                    <div className="mt-3 text-center">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, latitude: 0, longitude: 0 }))}
                        className="text-xs text-gray-500 hover:text-gray-700 underline font-lucy-rose"
                      >
                        Clear Coordinates
                      </button>
                    </div>
                  )}
                  
                  {/* Map Button */}
                  <button
                    type="button"
                    onClick={() => setShowLocationPicker(true)}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-lucy-rose"
                  >
                    <Map className="h-5 w-5" />
                    <span>{hasLocationCoordinates ? 'Change Location on Map' : 'Select Location on Map'}</span>
                  </button>
                  
                  {/* Coordinates Display */}
                  {hasLocationCoordinates && (
                    <div className="text-xs text-gray-600 font-lucy-rose">
                      <strong>Current Coordinates:</strong> {
                        !isNaN(Number(formData.latitude)) && !isNaN(Number(formData.longitude))
                          ? `${Number(formData.latitude).toFixed(6)}, ${Number(formData.longitude).toFixed(6)}`
                          : 'Invalid coordinates'
                      }
                    </div>
                  )}
                </div>
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600 font-lucy-rose">{errors.location}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                required
                rows={4}
                maxLength={500}
                value={formData.description}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose resize-none ${
                  errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Describe your event, its significance, and what attendees can expect..."
              />
              <div className="mt-1 text-xs text-gray-500 text-right font-lucy-rose">{formData.description.length}/500</div>
              {errors.description && (
                <p className="mt-1 text-sm text-red-600 font-lucy-rose">{errors.description}</p>
              )}
            </div>

            <div>
                              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  Full Address (Pre-filled from map)
                </label>
              <textarea
                id="address"
                name="address"
                required
                rows={2}
                value={formData.address}
                readOnly
                className={`w-full px-4 py-3 border rounded-lg bg-gray-50 font-lucy-rose resize-none ${
                  errors.address ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Address is automatically populated from map coordinates"
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600 font-lucy-rose">{errors.address}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  Start Date & Time *
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="datetime-local"
                    id="startDate"
                    name="startDate"
                    required
                    value={formData.startDate}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose ${
                      errors.startDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600 font-lucy-rose">{errors.startDate}</p>
                )}
                <div className="mt-2 flex items-center gap-2 text-xs font-lucy-rose">
                  <button
                    type="button"
                    className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    onClick={() => {
                      const now = new Date();
                      const iso = new Date(now.getTime() - now.getTimezoneOffset()*60000).toISOString().slice(0,16);
                      setFormData(prev => ({ ...prev, startDate: iso }));
                      if (errors.startDate) setErrors(prev => ({ ...prev, startDate: '' }));
                    }}
                  >Today</button>
                  <button
                    type="button"
                    className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    onClick={() => {
                      const now = new Date();
                      now.setDate(now.getDate() + 1);
                      const iso = new Date(now.getTime() - now.getTimezoneOffset()*60000).toISOString().slice(0,16);
                      setFormData(prev => ({ ...prev, startDate: iso }));
                      if (errors.startDate) setErrors(prev => ({ ...prev, startDate: '' }));
                    }}
                  >Tomorrow</button>
                </div>
              </div>

              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  End Date & Time *
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="datetime-local"
                    id="endDate"
                    name="endDate"
                    required
                    value={formData.endDate}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose ${
                      errors.endDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.endDate && (
                  <p className="mt-1 text-sm text-red-600 font-lucy-rose">{errors.endDate}</p>
                )}
                <div className="mt-2 flex items-center gap-2 text-xs font-lucy-rose">
                  <button
                    type="button"
                    className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                    disabled={!formData.startDate}
                    onClick={() => {
                      if (!formData.startDate) return;
                      const start = new Date(formData.startDate);
                      const plus = new Date(start.getTime());
                      plus.setDate(plus.getDate() + 1);
                      const iso = new Date(plus.getTime() - plus.getTimezoneOffset()*60000).toISOString().slice(0,16);
                      setFormData(prev => ({ ...prev, endDate: iso }));
                      if (errors.endDate) setErrors(prev => ({ ...prev, endDate: '' }));
                    }}
                  >Set +1 day</button>
                </div>
              </div>

              <div>
                <label htmlFor="expectedAttendance" className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">
                  Expected Attendance *
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="number"
                    id="expectedAttendance"
                    name="expectedAttendance"
                    required
                    min="1"
                    value={formData.expectedAttendance}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose ${
                      errors.expectedAttendance ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Number of attendees"
                  />
                </div>
                {errors.expectedAttendance && (
                  <p className="mt-1 text-sm text-red-600 font-lucy-rose">{errors.expectedAttendance}</p>
                )}
              </div>
            </div>

            {/* Event Thumbnail Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4 font-lucy-rose">
                Event Thumbnail
              </label>
              
              {/* Current thumbnail display */}
              {(thumbnailPreview || existingThumbnail) && (
                <div className="mb-4">
                  <div className="relative inline-block">
                    <div className="w-48 h-32 bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={thumbnailPreview || existingThumbnail || ''}
                        alt="Event thumbnail"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleThumbnailRemove}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Upload button */}
              {!thumbnailPreview && !existingThumbnail && (
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
                  onDragOver={(e) => { e.preventDefault(); (e.currentTarget as HTMLDivElement).classList.add('border-dpn-pink'); }}
                  onDragLeave={(e) => { (e.currentTarget as HTMLDivElement).classList.remove('border-dpn-pink'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    (e.currentTarget as HTMLDivElement).classList.remove('border-dpn-pink');
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      const allowed = ['image/jpeg','image/jpg','image/png','image/webp'];
                      if (!allowed.includes(file.type)) {
                        setErrors(prev => ({ ...prev, thumbnail: 'Thumbnail must be JPEG, PNG, or WebP format' }));
                        return;
                      }
                      const maxSize = 2 * 1024 * 1024;
                      if (file.size > maxSize) {
                        setErrors(prev => ({ ...prev, thumbnail: 'Thumbnail must be less than 2MB' }));
                        return;
                      }
                      setThumbnail(file);
                      const previewUrl = URL.createObjectURL(file);
                      setThumbnailPreview(previewUrl);
                      if (errors.thumbnail) setErrors(prev => ({ ...prev, thumbnail: '' }));
                    }
                  }}
                >
                  <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <div className="space-y-2">
                    <label 
                      htmlFor="thumbnail-upload"
                      className="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-dpn-pink hover:bg-dpn-raspberry transition-colors disabled:opacity-50"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Thumbnail
                    </label>
                    <input
                      id="thumbnail-upload"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleThumbnailChange}
                      className="hidden"
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 font-lucy-rose">
                    or drag and drop an image here (JPEG, PNG, WebP - Max 2MB)
                  </p>
                </div>
              )}

              {/* Change thumbnail button when thumbnail exists */}
              {(thumbnailPreview || existingThumbnail) && (
                <div className="mt-4">
                  <label 
                    htmlFor="thumbnail-upload-change"
                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Change Thumbnail
                  </label>
                  <input
                    id="thumbnail-upload-change"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleThumbnailChange}
                    className="hidden"
                    disabled={loading}
                  />
                </div>
              )}

              {/* Error message */}
              {errors.thumbnail && (
                <p className="mt-2 text-sm text-red-600 font-lucy-rose flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.thumbnail}
                </p>
              )}

              <p className="mt-2 text-xs text-gray-500 font-lucy-rose">
                The thumbnail will be the main image displayed for your event in listings and previews.
              </p>
            </div>

            {/* Admin inline: User Info Section */}
            {isAdmin && (
              <div className="mt-8 pt-8 border-t">
                <h4 className="font-runtime text-xl font-semibold text-gray-900 mb-4">User Info (optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">First Name</label>
                    <input
                      type="text"
                      value={userInfo.firstName}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">Last Name</label>
                    <input
                      type="text"
                      value={userInfo.lastName}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose"
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">Phone</label>
                    <input
                      type="tel"
                      value={userInfo.phone}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose"
                      placeholder="e.g., +91-XXXXXXXXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">Email</label>
                    <input
                      type="email"
                      value={userInfo.email}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose"
                      placeholder="user@example.com"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-lucy-rose">Role</label>
                  <select
                    value={userInfo.role}
                    onChange={(e) => setUserInfo(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose"
                  >
                    <option value="COMMITTEE">Committee</option>
                    <option value="VENDOR">Vendor</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-2 font-lucy-rose">If filled, the event will be created along with this user.</p>
                </div>
              </div>
            )}

            {/* Live Preview */}
            <div className="mt-6">
              <h4 className="font-runtime text-lg font-semibold text-gray-900 mb-3">Live Preview</h4>
              <div className="border rounded-lg overflow-hidden">
                <div className="w-full h-40 bg-gray-100">
                  {thumbnailPreview || existingThumbnail ? (
                    <img src={thumbnailPreview || existingThumbnail || ''} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 font-lucy-rose text-sm">No image</div>
                  )}
                </div>
                <div className="p-4">
                  <h5 className="font-semibold text-gray-900">{formData.title || 'Untitled Event'}</h5>
                  <p className="text-sm text-gray-600 mt-1">
                    {formData.startDate ? new Date(formData.startDate).toLocaleString() : 'Start TBA'}
                    {formData.endDate ? ` — ${new Date(formData.endDate).toLocaleString()}` : ''}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{formData.location || 'Location TBA'}</p>
                  <p className="text-sm text-gray-600 mt-1">{formData.expectedAttendance ? `${formData.expectedAttendance.toLocaleString()} expected` : 'Attendance TBA'}</p>
                </div>
              </div>
            </div>


            {/* Submit Button */}
            <div className="sticky bottom-0 bg-white pt-4 mt-6">
              <div className="flex items-center justify-between border-t pt-4">
                {(!hasLocationCoordinates) && (
                  <p className="text-xs text-orange-600 font-lucy-rose">Select location on map or enter valid coordinates to enable submission.</p>
                )}
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-3 text-gray-600 hover:text-gray-800 font-lucy-rose transition-colors"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !hasLocationCoordinates}
                    className={`px-8 py-3 rounded-lg font-lucy-rose font-medium transition-all duration-200 ${
                      loading || !hasLocationCoordinates
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-dpn-pink hover:bg-dpn-raspberry text-white hover:shadow-lg'
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <span>{event ? 'Update Event' : 'Create Event'}</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <LocationPicker
          onLocationSelect={handleLocationSelect}
          initialLocation={
            hasLocationCoordinates
              ? {
                  latitude: formData.latitude,
                  longitude: formData.longitude,
                  address: formData.address,
                  location: formData.location
                }
              : null
          }
          onClose={() => setShowLocationPicker(false)}
          showModal={true}
        />
      )}
    </>
  );
};

export default EventFormWithMap;